import type {Mark} from "@tiptap/pm/model";
import type {CSSProperties} from "react";

/**
 * The look of an inline variable chip, wherever one is authored.
 *
 * Printouts and guest surveys both let an author drop a value into running text, and a variable has
 * to read as a variable in both: sized in em so it follows the surrounding text (headings,
 * captions), and violet — deliberately off every document and survey ink ramp, so an unresolved
 * placeholder can never be mistaken for content that will actually print or be read by a guest.
 *
 * It lives here rather than inside either feature because the second one copying it is how the two
 * start disagreeing about what a variable looks like.
 */
export const variableChipSx = {
    backgroundColor: "#F4F0FF",
    borderRadius: "5px",
    boxDecorationBreak: "clone",
    color: "#6D28D9",
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
    fontSize: "0.82em",
    fontWeight: 700,
    lineHeight: 1.4,
    padding: "0.08em 0.4em",
    whiteSpace: "pre-wrap",
    WebkitBoxDecorationBreak: "clone",
};

/**
 * The pill for a chip that sits in a sentence the author is writing.
 *
 * The trick is which axis costs layout. On an inline box, vertical padding is free — it overflows
 * the line box without changing it — so the pill gets its height for nothing. Only horizontal
 * padding is layout, so that is spent sparingly: 0.16em is enough to read as a pill and small
 * enough that the comma an author types after a variable does not visibly jump.
 *
 * The earlier version bought symmetry by painting the background outside the box, from an
 * absolutely positioned pseudo-element inset past the text. It cost nothing in layout, but a
 * background that reaches beyond its own box reaches over whatever comes next — and what comes
 * next is usually punctuation, which abuts the word with no gap. It also left no honest edge to
 * mark selection against, which forced selection to be nothing but a heavier tint.
 *
 * Real padding gives the box back: the pill stops where the pill stops, the comma is beside it
 * rather than under it, and there is an edge to put a ring on.
 */
export const inlineVariableChipSx = {
    backgroundColor: "#F4F0FF",
    borderRadius: "4px",
    boxDecorationBreak: "clone",
    color: "#6D28D9",
    fontWeight: 600,
    padding: "0.12em 0.16em",
    whiteSpace: "nowrap",
    WebkitBoxDecorationBreak: "clone",
} as const;

/** Stronger tint + ring while the chip is the editor's node selection. */
export const selectedVariableChipSx = {
    backgroundColor: "#EDE9FE",
    boxShadow: "0 0 0 2px #7C3AED",
};

/**
 * The chip, with the editor's selection state folded in.
 *
 * The ring sits on the padding edge, which only exists because the pill takes real padding — the
 * reason selection can be marked at all rather than being nothing but a heavier tint.
 */
export const inlineVariableChipSxFor = (selected: boolean) => ({
    ...inlineVariableChipSx,
    ...(selected ? selectedVariableChipSx : {}),
});

/** Preview-only state for variables the resolver could not fill. */
export const unresolvedVariableChipSx = {
    backgroundColor: "#FEE2E2",
    color: "#B42318",
};

/** Under @media print any chip that still exists must degrade to plain document ink. */
export const printVariableChipSx = {
    backgroundColor: "transparent",
    borderRadius: 0,
    boxShadow: "none",
    color: "inherit",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    padding: 0,
};

/**
 * Text marks applied to the variable node (bold, color, highlight, ...) restyle the chip itself,
 * so authors see that the resolved value will carry their formatting.
 *
 * The survey schema only carries bold, italic and underline today. The rest of the switch stays:
 * it costs nothing on a mark that never arrives, and it is what keeps a chip honest the day the
 * schema grows a colour or a highlight.
 */
export const getVariableChipStyle = (marks: readonly Mark[]): CSSProperties => {
    const style: CSSProperties = {};
    const textDecorations: string[] = [];

    marks.forEach((mark) => {
        switch (mark.type.name) {
            case "bold":
                style.fontWeight = 800;
                break;
            case "italic":
                style.fontStyle = "italic";
                break;
            case "underline":
                textDecorations.push("underline");
                break;
            case "strike":
                textDecorations.push("line-through");
                break;
            case "textStyle":
                if (typeof mark.attrs.color === "string" && mark.attrs.color) {
                    style.color = mark.attrs.color;
                }
                break;
            case "highlight":
                if (typeof mark.attrs.color === "string" && mark.attrs.color) {
                    style.backgroundColor = mark.attrs.color;
                }
                break;
            default:
                break;
        }
    });

    if (textDecorations.length) {
        style.textDecoration = textDecorations.join(" ");
    }

    return style;
};
